import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator, Send, Share2 } from "lucide-react";
import { Link } from "react-router-dom";

const materials = [
  { name: "خشب محلي", factor: 1.0, icon: "🪵" },
  { name: "خشب مستورد", factor: 1.6, icon: "🌲" },
  { name: "ألمنيوم عادي", factor: 0.8, icon: "🔩" },
  { name: "ألمنيوم مقاوم", factor: 1.3, icon: "🛡️" },
  { name: "حديد مشغول", factor: 1.1, icon: "⚙️" },
  { name: "حديد عادي", factor: 0.7, icon: "🔧" },
];

const complexityLabels = ["بسيط", "متوسط", "معقد", "مخصص"];
const complexityIcons = ["🟢", "🟡", "🟠", "🔴"];

export default function CostEstimator() {
  const [area, setArea] = useState(2);
  const [materialIdx, setMaterialIdx] = useState(0);
  const [complexity, setComplexity] = useState(1);

  const basePricePerSqm = 25000;
  const price = useMemo(() => 
    Math.round(area * basePricePerSqm * materials[materialIdx].factor * (1 + complexity * 0.4)),
    [area, materialIdx, complexity]
  );
  const formatted = price.toLocaleString("ar-YE");

  const shareQuote = () => {
    const text = `تقدير تكلفة من مجمع الشويع:\n${materials[materialIdx].name} - ${area} م² - ${complexityLabels[complexity]}\nالتكلفة التقريبية: ${formatted} ر.ي`;
    if (navigator.share) {
      navigator.share({ title: "تقدير تكلفة", text });
    } else {
      navigator.clipboard.writeText(text);
    }
  };

  return (
    <Card className="border-primary/20 overflow-hidden">
      <CardHeader className="pb-3 bg-gradient-woody/5">
        <CardTitle className="text-lg flex items-center gap-2">
          <Calculator className="h-5 w-5 text-primary" />
          أداة تقدير التكلفة التفاعلية
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 pt-5">
        <div className="space-y-2">
          <Label className="text-sm">المساحة: <strong className="text-primary">{area} م²</strong></Label>
          <Slider value={[area]} onValueChange={v => setArea(v[0])} min={1} max={50} step={1} aria-label="المساحة بالمتر المربع" />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>1 م²</span><span>50 م²</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-sm">نوع المادة</Label>
          <div className="grid grid-cols-3 gap-2">
            {materials.map((m, i) => (
              <button
                key={i}
                onClick={() => setMaterialIdx(i)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex flex-col items-center gap-1 ${i === materialIdx ? "bg-primary text-primary-foreground shadow-woody" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
              >
                <span className="text-lg">{m.icon}</span>
                {m.name}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-sm">
            درجة التعقيد: {complexityIcons[complexity]} <strong>{complexityLabels[complexity]}</strong>
          </Label>
          <Slider value={[complexity]} onValueChange={v => setComplexity(v[0])} min={0} max={3} step={1} aria-label="مستوى تعقيد التصميم" />
          <div className="flex justify-between text-xs text-muted-foreground">
            {complexityLabels.map((l, i) => <span key={i}>{l}</span>)}
          </div>
        </div>

        <div className="bg-gradient-woody rounded-xl p-5 text-center">
          <p className="text-primary-foreground/80 text-sm mb-1">التكلفة التقريبية</p>
          <p className="text-4xl font-black text-primary-foreground">{formatted}</p>
          <p className="text-primary-foreground/70 text-xs mt-1">ريال يمني (تقريبي)</p>
        </div>

        <div className="flex gap-2">
          <Link to="/طلب-تصميم" className="flex-1">
            <Button className="w-full bg-gradient-woody text-primary-foreground hover:opacity-90" size="sm">
              <Send className="h-4 w-4 ml-1" />
              اطلب عرض سعر
            </Button>
          </Link>
          <Button variant="outline" size="sm" onClick={shareQuote} className="border-primary/30">
            <Share2 className="h-4 w-4" />
          </Button>
        </div>

        <p className="text-xs text-muted-foreground text-center">* الأسعار تقريبية وقد تختلف حسب التفاصيل الدقيقة للمشروع</p>
      </CardContent>
    </Card>
  );
}
